import { useEffect } from 'react';
import { DefaultTheme, ThemeProvider } from 'expo-router';
import Head from 'expo-router/head';
import { Stack } from 'expo-router/stack';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import * as Sentry from '@sentry/react-native';
import { APP_VERSION } from '@funpaper/shared';

import { getAuthToken } from '@/lib/auth';
import { ensureGuestSession } from '@/lib/api/auth';
import { initializeAnalytics } from '@/lib/analytics';

const WEB_RELEASE = `v${APP_VERSION}`;
const APP_ENV = process.env.EXPO_PUBLIC_APP_ENV ?? 'remote-dev';
const IS_REMOTE_DEV = APP_ENV !== 'production';
const APP_DISPLAY_NAME = IS_REMOTE_DEV ? 'Funpaper Dev' : 'Funpaper';
const APP_TITLE = `${APP_DISPLAY_NAME} — Ide Bermain Sambil Belajar`;
const THEME_COLOR = IS_REMOTE_DEV ? '#0a7473' : '#563f7a';

// Sentry hanya aktif saat EXPO_PUBLIC_SENTRY_DSN di-bake saat build
// (lihat build:web:production / build:web:remote-dev). Tanpa DSN (dev lokal)
// SDK idle — tidak ada network call, tidak ada error.
if (process.env.EXPO_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
    // environment mengikuti konvensi APP_ENV yang sudah ada (remote-dev vs production).
    environment: process.env.EXPO_PUBLIC_APP_ENV ?? (__DEV__ ? 'development' : 'production'),
    release: `funpaper-web@${WEB_RELEASE}`,
    // App dipakai anak-anak: jangan kirim PII (email/cookie) ke Sentry.
    sendDefaultPii: false,
    tracesSampleRate: process.env.EXPO_PUBLIC_APP_ENV === 'production' ? 0.2 : 1.0,
  });
}

SplashScreen.preventAutoHideAsync();

function RootLayout() {
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    initializeAnalytics();

    if (!('serviceWorker' in navigator)) return;

    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error: unknown) => {
      console.warn('[PWA] service worker registration failed', error);
    });
  }, []);

  // Plan 17 (deferred login): tidak ada lagi hard redirect ke /login.
  // First launch → bootstrap guest session diam-diam; user baru diminta
  // login saat trial habis (TrialExpired wall) atau sukarela dari Akun.
  useEffect(() => {
    if (getAuthToken()) return;
    ensureGuestSession().catch((err: unknown) => {
      console.warn('[auth] guest bootstrap failed', err);
      // Gagal bootstrap (offline/server down): biarkan screen jalan;
      // apiFetch akan error dengan pesan ramah saat user berinteraksi.
    });
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Head>
        <title>{APP_TITLE}</title>
        <meta
          name="description"
          content="Rekomendasi worksheet untuk bermain sambil belajar bersama anak."
        />
        <meta name="theme-color" content={THEME_COLOR} />
        <meta name="application-version" content={WEB_RELEASE} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="application-name" content={APP_DISPLAY_NAME} />
        <meta name="apple-mobile-web-app-title" content={APP_DISPLAY_NAME} />
        <link rel="manifest" href="/manifest.webmanifest" />
        <link rel="apple-touch-icon" href="/icons/icon-192.png?v=tm5" />
      </Head>
      {/*
        Dark mode dimatikan (Fase 1 redesign): palet dark sebelumnya memakai
        background ungu gelap #1a1525 yang membuat teks susah terbaca. App
        sekarang selalu light dengan background putih (ala Notion); warna
        brand purple hanya dipakai sebagai aksen (tombol, pill, chip).
      */}
      <ThemeProvider value={DefaultTheme}>
        {/*
          GestureHandlerRootView is required for the swipe gesture in
          react-native-deck-swiper (used by SwipeStack in home card stack).
          (BottomSheetModalProvider removed in Phase 3.6 — RejectReasonSheet
          was replaced by the non-blocking RejectReasonBanner.)
        */}
        <Stack screenOptions={{ headerShown: false }}>
          {/* Routes are auto-discovered via Expo Router file-based routing. */}
        </Stack>
        <StatusBar style="dark" />
      </ThemeProvider>
    </GestureHandlerRootView>
  );
}

// Wrap memberi error boundary Sentry: render crash tertangkap
// sebagai issue (dengan release + sourcemap), bukan white-screen senyap.
export default Sentry.wrap(RootLayout);
